import { ExclamationCircleFilled } from '@ant-design/icons'
import { faPills, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal as AntModal, Button, Card, Col, Modal, Row, Select, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect } from 'react'
import DesktopMedicalHistoryNav from './Nav'
import DatePicker from '../../../Reusable Components/DatePicker'
import useGeneralMeds from '../../hooks/useGeneralMeds'
import '../style/DesktopGeneralMeds.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopGeneralMeds = ({ clientForm, data, setFacet, setSaving }) => {
  const {
    medsList,
    nappiData,
    nappiLoading,
    setNappiSearchFilter,
    handleAddMedication,
    handleUpdateMedication,
    handleRemoveMedication,
    handleSubmit,
    showMedicationModal,
    setShowMedicationModal,
    editingMedication,
    setEditingMedication,
    tempMedDates,
    setTempMedDates
  } = useGeneralMeds({ clientForm, setFacet, setSaving })

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  const [showMedStartDatePicker, setShowMedStartDatePicker] = useState(false)
  const [showMedEndDatePicker, setShowMedEndDatePicker] = useState(false)

  const showDeleteMedicationConfirm = (medication, index) => {
    AntModal.confirm({
      title: 'Remove Medication',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div>
          <p>
            Are you sure you want to remove <strong>{medication.med.name}</strong>?
          </p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveMedication(index)
      }
    })
  }

  return (
    <div className='desktop-general-meds-container'>
      <DesktopSectionHeader
        icon={faPills}
        title='General Medications'
        subtitle='Track all medications you are currently taking or have taken in the past'
      />

      <div className='desktop-general-meds-content'>
        {/* Add New Medication Button */}
        <Card className='desktop-add-medication-card'>
          <Button
            type='primary'
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setShowMedicationModal(true)}
            size='large'
            className='desktop-add-medication-btn'
          >
            Add Medication
          </Button>
        </Card>

        {/* Medications List */}
        <Row gutter={[24, 24]}>
          {medsList.map((medication, index) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={index}>
              <Card className='desktop-medication-card'>
                <div className='desktop-medication-header'>
                  <div className='desktop-medication-icon'>
                    <FontAwesomeIcon icon={faPills} />
                  </div>
                  <Button
                    danger
                    type='text'
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => showDeleteMedicationConfirm(medication, index)}
                    className='desktop-remove-medication-btn'
                  />
                </div>

                <div className='desktop-medication-info'>
                  <h4 className='desktop-medication-name'>{medication.med.name}</h4>
                  <div className='desktop-medication-dates'>
                    <Typography.Text type='secondary'>
                      <strong>Started:</strong>{' '}
                      {medication.startDate ? dayjs(medication.startDate).format('MMM YYYY') : 'Unknown'}
                    </Typography.Text>
                    <Typography.Text type='secondary'>
                      <strong>Ended:</strong>{' '}
                      {medication.endDate ? dayjs(medication.endDate).format('MMM YYYY') : 'Ongoing'}
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {medsList.length === 0 && (
          <Card className='desktop-empty-state'>
            <div className='desktop-empty-content'>
              <FontAwesomeIcon icon={faPills} className='desktop-empty-icon' />
              <h3>No Medications Added</h3>
              <p>Click "Add Medication" to start tracking your current and past medications.</p>
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Medication Modal */}
      <Modal
        title={editingMedication !== null ? 'Edit Medication' : 'Add Medication'}
        open={showMedicationModal}
        onCancel={() => {
          setShowMedicationModal(false)
          setEditingMedication(null)
          setTempMedDates({ startDate: null, endDate: null })
        }}
        footer={null}
        className='desktop-medication-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Medication Name</label>
            <Select
              showSearch
              placeholder='Search for medication...'
              style={{ width: '100%' }}
              loading={nappiLoading}
              onSearch={setNappiSearchFilter}
              filterOption={false}
              notFoundContent={nappiLoading ? 'Searching...' : 'No medications found'}
            >
              {nappiData.map((med, index) => (
                <Select.Option key={index} value={med.genericName}>
                  {med.genericName}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>Start Date</label>
                <Button block onClick={() => setShowMedStartDatePicker(true)} className='desktop-date-button'>
                  {tempMedDates.startDate ? dayjs(tempMedDates.startDate).format('MMM YYYY') : 'Select start date'}
                </Button>
              </div>
            </Col>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>End Date (Optional)</label>
                <Button block onClick={() => setShowMedEndDatePicker(true)} className='desktop-date-button'>
                  {tempMedDates.endDate ? dayjs(tempMedDates.endDate).format('MMM YYYY') : 'Ongoing'}
                </Button>
              </div>
            </Col>
          </Row>

          <div className='desktop-modal-actions'>
            <Space>
              <Button onClick={() => setShowMedicationModal(false)}>Cancel</Button>
              <Button type='primary' onClick={handleAddMedication}>
                {editingMedication !== null ? 'Update' : 'Add'} Medication
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Date Pickers */}
      <DatePicker
        isVisible={showMedStartDatePicker}
        onDateSelect={(date) => {
          setTempMedDates((prev) => ({ ...prev, startDate: date.toISOString() }))
          setShowMedStartDatePicker(false)
        }}
        onCancel={() => setShowMedStartDatePicker(false)}
        mode='month'
        maximumDate={new Date()}
      />

      <DatePicker
        isVisible={showMedEndDatePicker}
        onDateSelect={(date) => {
          setTempMedDates((prev) => ({ ...prev, endDate: date.toISOString() }))
          setShowMedEndDatePicker(false)
        }}
        onCancel={() => setShowMedEndDatePicker(false)}
        mode='month'
        maximumDate={new Date()}
      />

      <DesktopMedicalHistoryNav
        prevFunction={() => setFacet(null)}
        nextFunction={handleSubmit}
        prevText='Back to Overview'
        nextText='Save Medications'
      />
    </div>
  )
}

export default DesktopGeneralMeds
